import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MdContentCopy, MdReply, MdDelete, MdCheck } from 'react-icons/md';
import { formatTime } from './ConversationListItem';
import { useIsMobile } from '../../hooks/useIsMobile';
import OptionsBottomSheet from './OptionsBottomSheet';

const MENU_Z_INDEX = 9999;

export default function MessageBubble({
  message,
  isOwn,
  isSent,
  senderDisplayName,
  onCopy,
  onReply,
  onDelete,
  onEdit,
  canEdit,
  t = (s) => s,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownRect, setDropdownRect] = useState(null);
  const menuRef = useRef(null);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const isMobile = useIsMobile();
  const longPressTimer = useRef(null);

  const openMenu = () => setMenuOpen(true);
  const clearLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  useEffect(() => {
    return () => clearLongPress();
  }, []);

  useEffect(() => {
    if (!menuOpen) {
      setDropdownRect(null);
      return;
    }
    if (!isMobile && triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setDropdownRect({ top: r.bottom + 4, left: r.left, width: 176 });
    }
    const handleClickOutside = (e) => {
      if (menuRef.current?.contains(e.target) || dropdownRef.current?.contains(e.target)) return;
      setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen, isMobile]);

  const sheetOptions = [
    { label: 'Kopiraj', icon: <MdContentCopy className="text-lg" />, onClick: () => onCopy?.(message?.content) },
    { label: 'Odgovori', icon: <MdReply className="text-lg" />, onClick: () => onReply?.(message) },
  ];
  if (canEdit && onEdit) sheetOptions.push({ label: 'Izmeni', icon: null, onClick: () => onEdit(message) });
  if (isOwn || onDelete) sheetOptions.push({ label: 'Obriši', icon: <MdDelete className="text-lg" />, onClick: () => onDelete?.(message), danger: true });

  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} group/bubble`}>
      <div className={`max-w-[85%] lg:max-w-xl ${isOwn ? 'order-2' : 'order-1'}`}>
        {!isOwn && senderDisplayName && (
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5 pl-1">
            {senderDisplayName}
          </p>
        )}
        <div className="relative" ref={menuRef}>
          <div
            ref={triggerRef}
            onContextMenu={(e) => {
              e.preventDefault();
              openMenu();
            }}
            onTouchStart={() => {
              if (!isMobile) return;
              clearLongPress();
              longPressTimer.current = setTimeout(openMenu, 400);
            }}
            onTouchEnd={clearLongPress}
            onTouchMove={clearLongPress}
            className={`
              px-4 py-2.5 rounded-2xl text-sm border border-transparent
              ${isOwn
                ? 'bg-primary-500 text-white rounded-br-md'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-bl-md'
              }
            `}
          >
            {message.content?.startsWith('data:image') ? (
              <img src={message.content} alt="Slika" className="max-w-full max-h-64 rounded-lg object-contain" />
            ) : (
              <p className="whitespace-pre-wrap break-words">{message.content}</p>
            )}
          </div>
          <span className={`text-xs mt-0.5 flex items-center gap-1 ${isOwn ? 'justify-end text-gray-500 dark:text-gray-400' : 'text-left text-gray-400 dark:text-gray-500'}`}>
            {formatTime(message.createdAt)}
            {isOwn && isSent && <MdCheck className="w-3.5 h-3.5 text-primary-500 dark:text-primary-400" aria-label="Poslato" />}
          </span>
          {isMobile && (
            <OptionsBottomSheet
              open={menuOpen}
              onClose={() => setMenuOpen(false)}
              options={sheetOptions}
              title="Poruka"
            />
          )}
          {!isMobile && menuOpen && dropdownRect && createPortal(
            <div
              ref={dropdownRef}
              className="fixed py-1 w-44 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-xl"
              style={{ top: dropdownRect.top, left: dropdownRect.left, zIndex: MENU_Z_INDEX }}
              onClick={(e) => e.stopPropagation()}
            >
              <button type="button" onClick={() => { onCopy?.(message?.content); setMenuOpen(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                <MdContentCopy className="text-base" />Kopiraj
              </button>
              <button type="button" onClick={() => { onReply?.(message); setMenuOpen(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                <MdReply className="text-base" />Odgovori
              </button>
              {canEdit && onEdit && (
                <button type="button" onClick={() => { onEdit(message); setMenuOpen(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                  Izmeni
                </button>
              )}
              {(isOwn || onDelete) && (
                <button type="button" onClick={() => { onDelete?.(message); setMenuOpen(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">
                  <MdDelete className="text-base" />Obriši
                </button>
              )}
            </div>,
            document.body
          )}
        </div>
      </div>
    </div>
  );
}
